import { useEffect, type ReactNode } from 'react';

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-10 justify-center text-[var(--color-ink-muted)]">
      <span
        className="inline-block h-4 w-4 rounded-full border-2 border-[var(--color-cyan-200)] border-t-[var(--color-blue-500)] animate-spin"
        aria-hidden
      />
      <span className="text-sm">{label}…</span>
    </div>
  );
}

export function ErrorBanner({ message, onDismiss }: { message: string; onDismiss?: () => void }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-xl border border-[var(--color-coral-200)] bg-[var(--color-coral-50)] px-4 py-3 text-[13px] text-[var(--color-coral-700)]"
    >
      <span className="mt-px font-bold" aria-hidden>
        !
      </span>
      <p className="flex-1">{message}</p>
      {onDismiss && (
        <button type="button" onClick={onDismiss} className="btn btn-ghost !px-2 !py-0.5 text-inherit">
          Dismiss
        </button>
      )}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="px-6 py-14 text-center">
      <h3 className="text-lg text-[var(--color-ink)]">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-[13px] leading-relaxed text-[var(--color-ink-muted)]">
        {description}
      </p>
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[rgba(30,42,63,0.35)] p-0 sm:p-6 backdrop-blur-[2px]"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`card w-full ${wide ? 'sm:max-w-2xl' : 'sm:max-w-md'} max-h-[92vh] overflow-hidden flex flex-col rounded-b-none sm:rounded-[var(--radius-card)]`}
        style={{ boxShadow: 'var(--shadow-raised)' }}
      >
        <header className="flex items-center justify-between border-b border-[var(--color-line)] px-5 py-4">
          <h2 className="text-lg">{title}</h2>
          <button type="button" onClick={onClose} className="btn btn-ghost !px-2" aria-label="Close">
            ✕
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
        {footer && (
          <footer className="flex justify-end gap-2 border-t border-[var(--color-line)] bg-[var(--color-paper)] px-5 py-3.5">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
